import { useState } from "react";
import { useGetPageFromPath } from "../../../../../utils/customHooks/genericHooks";
import {
  useShouldHideProfitCategories,
  useTrendChartBarRangesForBarstacks,
} from "../../../../../utils/customHooks/trendChartHooks";
import TrendChartBar from "./TrendChartBar";
import { TrendChartLeftAxis, TrendChartRightAxis } from "../TrendChartAxis";
import { useShowTrendLineOnThisPage } from "./TrendLine/trendLineUtils";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import RenderIfVisible from "react-render-if-visible";
import { useMediaQuery } from "@mui/material";

export function TrendChartBars({
  isClickDisabled,
  isLoaderAnimationEnabled,
}: {
  isClickDisabled: boolean;
  isLoaderAnimationEnabled: boolean;
}) {
  const { isTrendsPage } = useGetPageFromPath();
  const [didAnimateOnce, setDidAnimateOnce] = useState(false);

  const trendChartBarsRanges = useTrendChartBarRangesForBarstacks();

  const [{ trendChart: trendChartConfig }] = useFlightConfig();

  const shouldShowAxes = trendChartConfig.showAxes && !isTrendsPage;
  // don't show left axis if we're showing profit categories (it's sized for profits OR losses only)
  const shouldShowLeftAxis = trendChartConfig.hideProfitCategories;
  // only show right axis if trend line feature is allowed on this page
  const shouldShowRightAxis = useShowTrendLineOnThisPage();

  return (
    <>
      {shouldShowAxes && shouldShowLeftAxis && (
        // left axis - Time Distribution (min) - shows a 60-minute height axis to measure the height of loss each bar
        <TrendChartLeftAxis />
      )}
      {trendChartBarsRanges.map((range, idx) => (
        <TrendChartBarIfVisible
          key={`trend-chart-bar-ge-${range.ge}-lt-${range.lt}`}
          {...{
            range,
            idx,
            didAnimateOnce,
            setDidAnimateOnce,
            isClickDisabled,
            isLoaderAnimationEnabled,
          }}
        />
      ))}
      {shouldShowAxes && shouldShowRightAxis && (
        // right axis - horizontal output target line
        <TrendChartRightAxis />
      )}
    </>
  );
}

function TrendChartBarIfVisible({
  range,
  idx,
  didAnimateOnce,
  setDidAnimateOnce,
  isClickDisabled,
  isLoaderAnimationEnabled,
}: {
  range: { ge: number; lt: number };
  idx: number;
  didAnimateOnce: boolean;
  setDidAnimateOnce: (value: boolean) => void;
  isClickDisabled: boolean;
  isLoaderAnimationEnabled: boolean;
}) {
  const { isTrendsPage } = useGetPageFromPath();
  const shouldHideProfitCategories = useShouldHideProfitCategories();
  const isPrint = useMediaQuery("print");
  // don't show horizontal target line if we're showing the target trend line instead
  const [flightConfig] = useFlightConfig();
  const shouldShowHorizontalTargetLine =
    !flightConfig.trendChart.showTargetTrendLine;

  const trendChartBar = (
    <TrendChartBar
      isClickDisabled={isClickDisabled}
      {...{
        range,
        idx,
        isTrendsPage,
        shouldHideProfitCategories,
        isLoaderAnimationEnabled,
        didAnimateOnce,
        setDidAnimateOnce,
        shouldShowHorizontalTargetLine,
      }}
    />
  );
  if (isPrint || idx < 13) {
    // using RenderIfVisible's `initialVisible` doesn't work when printing with Ctrl+P, so just render all bars in print mode
    return trendChartBar;
  }
  return (
    <RenderIfVisible
      key={`trend-chart-bar-ge-${range.ge}-lt-${range.lt}`}
      placeholderElement="div"
      placeholderElementClass="trendChartBarPlaceholder trend-chart-bar"
      stayRendered={true}
    >
      {trendChartBar}
    </RenderIfVisible>
  );
}
